interface Step<T = unknown> {
  id: string;
  fields: T[];
}

interface StepperNavigationOptions {
  isStepValid: StepValidationFn;
  onSectionChange: StepSectionsFn;
  buttons: {
    prev: HTMLElement;
    next: HTMLElement;
    submit: HTMLElement;
  };
}

type StepChangeListener = (index: number) => void;
type StepValidationFn = (localIndex: number) => boolean;
type StepSectionsFn = (localIndex: number) => void;


export class SteppedForm {
  private currentIndex = 0;
  private listeners: StepChangeListener[] = [];

  constructor(
    private steps: Step[],
    private _globalTotal?: number,
    private _startOffset: number = 0
  ) {
    if (!steps.length) throw new Error("Stepper requires steps");
  }

  get currentStep(): Step {
    return this.steps[this.currentIndex];
  }

  get localIndex(): number {
    return this.currentIndex;
  }

  get currentStepIndex(): number {
    return this._startOffset + this.currentIndex;
  }

  get totalSteps(): number {
    return this._globalTotal ?? this.steps.length;
  }

  onStepChange(listener: StepChangeListener): void {
    this.listeners.push(listener);
    listener(this.currentStepIndex); // initial sync
  }

  private notify(): void {
    this.listeners.forEach((l) => l(this.currentStepIndex));
  }

  next(): void {
    if (this.isLast()) return;
    this.currentIndex++;
    this.notify();
  }

  prev(): void {
    if (this.isFirst()) return;
    this.currentIndex--;
    this.notify();
  }

  isFirst(): boolean {
    return this.currentIndex === 0;
  }

  isLast(): boolean {
    return this.currentIndex === this.steps.length - 1;
  }
}

export class StepIndicator {
  private indicators: HTMLElement[] = [];
  private liveRegion!: HTMLElement;

  constructor(
    private container: HTMLElement,
    private stepper: SteppedForm
  ) {
    this.render();
    this.stepper.onStepChange((index) => this.update(index));
  }

  private render(): void {
    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild);
    }

    this.container.setAttribute("role", "list");

    for (let i = 0; i < this.stepper.totalSteps; i++) {
      const indicator = document.createElement("span");
      indicator.setAttribute("role", "listitem");
      indicator.className = "step-indicator";
      indicator.setAttribute("aria-label", `Step ${i + 1}`);
      this.indicators.push(indicator);
      this.container.appendChild(indicator);
    }

    this.liveRegion = document.createElement("div");
    this.liveRegion.className = "sr-only";
    this.liveRegion.setAttribute("aria-live", "polite");
    this.container.appendChild(this.liveRegion);
    this.update(this.stepper.currentStepIndex);
  }

  private update(currentIndex: number): void {
    this.indicators.forEach((indicator, i) => {
      indicator.classList.toggle("active", i === currentIndex);
      indicator.classList.toggle("completed", i < currentIndex);

      if (i === currentIndex) {
        indicator.setAttribute("aria-current", "step");
      } else {
        indicator.removeAttribute("aria-current");
      }
    });

    this.liveRegion.textContent =
      `Step ${currentIndex + 1} of ${this.stepper.totalSteps}`;
  }
}

export class StepperNavigation {
  readonly container: HTMLElement;

  constructor(
    private stepper: SteppedForm,
    private options: StepperNavigationOptions
  ) {
    this.container = document.createElement("div");
    this.container.className = "form-nav";

    this.wireButtons();

    // onStepChange fires the listener immediately with the initial index,
    // so both sections and nav buttons are correct from the first render.
    this.stepper.onStepChange(() => {
      this.options.onSectionChange(this.stepper.localIndex);
      this.render();
    });
  }

  private wireButtons(): void {
    const { prev, next } = this.options.buttons;

    next.onclick = () => {
      if (this.options.isStepValid(this.stepper.localIndex)) {
        this.stepper.next();
      }
    };

    prev.onclick = () => this.stepper.prev();
  }

  private render(): void {
    const { prev, next, submit } = this.options.buttons;
    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild);
    }

    if (this.stepper.isLast()) this.container.append(submit);
    if (!this.stepper.isFirst()) this.container.append(prev);
    if (!this.stepper.isLast()) this.container.append(next);
  }
}